import React from 'react';
import {
  Box,
  Divider,
  LinearProgress,
  SxProps,
  Typography,
  useTheme,
} from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { getBnplNextMonthSummaryCardStyles } from './style';

function formatClpInteger(value: number): string {
  return new Intl.NumberFormat('es-CL', {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(value);
}

export type BnplNextMonthSummaryCardProps = {
  label: string;
  helperText?: string;
  headlineAmount: number | null;
  paidAmount: number | null;
  referenceTotalAmount: number | null;
  headerIcon?: React.ReactNode;
  sxProps?: SxProps;
};

function BnplNextMonthSummaryCard({
  label,
  helperText,
  headlineAmount,
  paidAmount,
  referenceTotalAmount,
  headerIcon,
  sxProps = {},
}: BnplNextMonthSummaryCardProps) {
  const theme = useTheme();
  const sx = getBnplNextMonthSummaryCardStyles(theme);

  const barValue = Math.min(
    100,
    Math.max(
      0,
      (referenceTotalAmount && paidAmount) ? (paidAmount / referenceTotalAmount) * 100 : 0,
    ),
  );

  return (
    <Box sx={{ ...sx.root, ...sxProps }} component="article" aria-label={label}>
      <Box sx={sx.card}>
        <Box sx={sx.headerRow}>
          <Box sx={sx.headerIconWrap} aria-hidden>
            {(headerIcon != null)
              ? headerIcon
              : <CalendarMonthOutlinedIcon sx={sx.headerIcon} />}
          </Box>
          <Typography component="div" variant="caption" fontWeight="bold" sx={sx.label}>
            {label}
          </Typography>
        </Box>

        <Typography component="p" fontWeight="bold" sx={sx.headline}>
          $
          {' '}
          {formatClpInteger(headlineAmount || 0)}
        </Typography>

        {helperText && (
          <Typography variant="caption" component="p" sx={sx.helperText}>
            {helperText}
          </Typography>
        )}

        <Divider sx={{ marginBlock: 2, borderColor: theme.palette.grey[300] }} />

        {paidAmount !== null && referenceTotalAmount ? (
          <Typography variant="caption" component="div" fontWeight="bold" color="primary" gutterBottom>
            Ya pagaste $
            {' '}
            {formatClpInteger(paidAmount)}
            {' '}
            de $
            {' '}
            {formatClpInteger(referenceTotalAmount)}
          </Typography>
        ) : (
          <Typography variant="caption" component="div" fontWeight="bold" color="primary" gutterBottom>
            Sin deuda activa
          </Typography>
        )}
        <LinearProgress variant="determinate" value={barValue} sx={{ ...sx.progress, marginTop: 2 }} />
      </Box>
    </Box>
  );
}

BnplNextMonthSummaryCard.defaultProps = {
  helperText: undefined,
  headerIcon: null,
  sxProps: {},
};

export default BnplNextMonthSummaryCard;
